import { Link, router, usePage } from "@inertiajs/react"
import { Music2, X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { dismissCreatorTiktokBannerPath, onboardingTiktokPath } from "@/routes"
import type { SharedProps } from "@/types"

/**
 * ConnectTiktokBanner — OKS-376.
 *
 * App-wide, non-blocking inline `Alert` rendered in `CreatorShell` just
 * below the top bar, ONLY when:
 *   1. `tiktok_association_optional === true` (the relaxation flag), AND
 *   2. `creator_tiktok_connected !== true` (no active TikTok account), AND
 *   3. `tiktok_connect_banner_dismissed !== true` (not dismissed this
 *      session).
 *
 * Returns `null` (no DOM) otherwise — including flag OFF, connected
 * creators, and non-creator/anon shells (those never render
 * CreatorShell anyway, but the prop guards make this self-contained).
 *
 * The banner is account-level (TikTok is per-user, not per-brand), so it
 * rides along across brand switches until the creator connects. Dismissal
 * is session-backed via `POST dismissCreatorTiktokBannerPath()` — NO
 * localStorage (Crew storage rule); it re-appears next login.
 *
 * Accessibility: color is never the sole signal (Music2 icon + text);
 * the dismiss control is a real `<button>` with an `aria-label` +
 * `sr-only` text. No focus trap — the banner is inline, not a modal.
 */
export function ConnectTiktokBanner() {
  const { t } = useTranslation()
  const { props: sharedProps } = usePage<Partial<SharedProps>>()

  const visible =
    sharedProps.tiktok_association_optional === true &&
    sharedProps.creator_tiktok_connected !== true &&
    sharedProps.tiktok_connect_banner_dismissed !== true

  if (!visible) return null

  const handleDismiss = () => {
    router.post(
      dismissCreatorTiktokBannerPath(),
      {},
      { preserveScroll: true, preserveState: false },
    )
  }

  return (
    <Alert
      role="status"
      aria-live="polite"
      data-testid="connect-tiktok-banner"
      className="rounded-none border-x-0 border-t-0"
    >
      <Music2 aria-hidden="true" />
      <AlertTitle>{t("tiktok_optional.banner.title")}</AlertTitle>
      <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <span className="flex-1">{t("tiktok_optional.banner.body")}</span>
        <div className="flex items-center gap-2">
          <Button asChild size="sm" data-testid="connect-tiktok-banner-cta">
            <Link href={onboardingTiktokPath()}>
              {t("tiktok_optional.banner.connect")}
            </Link>
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleDismiss}
            aria-label={t("tiktok_optional.banner.dismiss")}
            data-testid="connect-tiktok-banner-dismiss"
          >
            <X aria-hidden="true" />
            <span className="sr-only">
              {t("tiktok_optional.banner.dismiss")}
            </span>
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  )
}
